import { MAX_EXT_ATTRIBUTES, UTM_PREFIX, EventTypes } from '../utils/consts.mjs';
import { BaseEvent } from './base-event.mjs';
import { flattenObject } from '../utils/flatten-object.mjs';

// © Sitecore Corporation A/S. All rights reserved. Sitecore® is a registered trademark of Sitecore Corporation A/S.
class PageViewEvent extends BaseEvent {
    /**
     * A class that extends from {@link BaseEvent} and has all the required functionality to send a VIEW event
     * @param args - Unified object containing the required properties
     */
    constructor(args) {
        const { channel, currency, pointOfSale, language, page } = args.eventData;
        super({
            channel,
            currency,
            language,
            page,
            pointOfSale,
        }, args.settings, args.id, args.infer);
        this.extensionData = {};
        this.eventData = args.eventData;
        this.urlSearchParams = new URLSearchParams(decodeURI(args.searchParams));
        if (args.extensionData)
            this.extensionData = flattenObject({ object: args.extensionData });
        const numberOfExtensionDataProperties = Object.entries(this.extensionData).length;
        if (numberOfExtensionDataProperties > MAX_EXT_ATTRIBUTES)
            throw new Error(`[IV-0005] This event supports maximum ${MAX_EXT_ATTRIBUTES} attributes. Reduce the number of attributes.`);
        this.eventApiClient = args.eventApiClient;
    }
    /**
     * Retrieves UTM parameters from the url query string
     * @returns - an object containing the UTM parameters if they exists
     */
    getUTMParameters() {
        const utmParameters = {};
        this.urlSearchParams.forEach((value, key) => {
            const param = key.toLowerCase();
            if (param.indexOf(UTM_PREFIX) === 0)
                utmParameters[param] = value;
        });
        return utmParameters;
    }
    /**
     * Gets the variant ID from the url if not passed by the developer
     * Gets the variant ID from the extension data if not found from the url
     * @returns - variant ID or null
     */
    getPageVariantId(pageVariantIdFromEventData, pageVariantIdFromExt) {
        if (pageVariantIdFromEventData)
            return pageVariantIdFromEventData;
        const pageVariantIdFromURL = this.urlSearchParams.get('variantid');
        if (pageVariantIdFromURL)
            return pageVariantIdFromURL;
        if (pageVariantIdFromExt)
            return pageVariantIdFromExt;
        return null;
    }
    /**
     * Returns the referrer if exists on page view event else null if we are on server and no referrer is on event, else
     * returns the href if on client side and the document referrer is different from the window location hostname
     * @returns - the referrer
     */
    getReferrer() {
        if (this.eventData.referrer)
            return this.eventData.referrer;
        if (typeof window === 'undefined')
            return null;
        if (!PageViewEvent.isFirstPageView || !document.referrer)
            return null;
        const { hostname, href } = new URL(document.referrer);
        return window.location.hostname !== hostname ? href : null;
    }
    /**
     * Maps parameters given as input to corresponding attributes send to the API
     * @returns the mapped object to be sent as payload
     */
    mapAttributes() {
        let viewPayload = {
            type: EventTypes.View,
        };
        if (this.settings.includeUTMParameters) {
            const utmParameters = this.getUTMParameters();
            viewPayload = { ...viewPayload, ...utmParameters };
        }
        const pageVariantId = this.getPageVariantId(this.eventData.pageVariantId, this.extensionData['pageVariantId']);
        if (pageVariantId !== null)
            viewPayload.ext = { ...viewPayload.ext, pageVariantId };
        if (Object.keys(this.extensionData).length > 0) {
            delete this.extensionData['pageVariantId'];
            viewPayload.ext = { ...viewPayload.ext, ...this.extensionData };
        }
        const referrer = this.getReferrer();
        if (referrer !== null)
            viewPayload = { ...viewPayload, referrer };
        return viewPayload;
    }
    /**
     * Sends the event to Sitecore CDP
     * @returns - A promise that resolves with either the Sitecore CDP response object or null
     */
    async send() {
        const baseAttr = this.mapBaseEventPayload();
        const eventAttrs = this.mapAttributes();
        const fetchBody = Object.assign({}, eventAttrs, baseAttr);
        PageViewEvent.isFirstPageView = false;
        return await this.eventApiClient.send(fetchBody);
    }
}
PageViewEvent.isFirstPageView = true;

export { PageViewEvent };
